import { toQueryString, useAsyncJobContext, useProtrakApi } from '../../Core';

const instanceBase = '/instances';
const getInstanceURL = id => `${instanceBase}/${id}`;
const getVersionURL = (id, versionId) =>
  `${instanceBase}/${id}/versions/${versionId}`;
const getInstanceMessagesURL = id =>
  `${instanceBase}/${id}/messages?take=1000&skip=0`;
const getInstanceVersionAttachments = (id, versionId) =>
  `${instanceBase}/${id}/versions/${versionId}/files`;

const instanceAttachmentsURL = id => `${instanceBase}/${id}/files`;

export const createEndpoint = (query, baseUrl) => {
  if (query) {
    const queryParams = toQueryString(query);
    const instanceBaseUrl = baseUrl || instanceBase;
    const endpoint = `${instanceBaseUrl}?${queryParams}`;

    return endpoint;
  }

  return null;
};

export function useInstanceDetailsForSections(id, sections, versionId = null) {
  const attributes = getAttributeNames(sections);
  const { state, run } = useProtrakApi(
    { requestConfig: instanceDetailsPromise, id, attributes, versionId },
    { defer: false }
  );
  return [state, run];
}

const addAttributesForDisplayCondition = (dc, attributes) => {
  if (!dc || !dc.rules || !dc.rules.length) {
    return;
  }
  dc.rules.forEach(rule => {
    rule.attributeFilterConditions.forEach(afc => {
      attributes.add(afc.attributeName);
    });
  });
};

const getAttributeNames = sections => {
  if (!sections || sections.length === 0) {
    return [];
  }

  let attributes = new Set();

  sections.forEach(section => {
    addAttributesForDisplayCondition(section.displayCondition, attributes);
    section.widgets.forEach(widget => {
      addAttributesForDisplayCondition(widget.displayCondition, attributes);
      widget.fields.forEach(field => {
        attributes.add(field.attributeName);
      });
    });
  });
  attributes.add('Geolocation');
  attributes.add('QRCode');
  return [...attributes];
};

const instanceDetailsPromise = ({ id, attributes, versionId }) => {
  const url = versionId ? getVersionURL(id, versionId) : getInstanceURL(id);
  return {
    endpoint: url,
    config: {
      method: 'GET',
      params: { attributes }
    }
  };
};

export const useInstanceDetails = ({
  instanceTypeName,
  attributeNames,
  attributeFilter,
  stateFilter,
  params
}) => {
  const { state, run } = useProtrakApi(
    {
      requestConfig: instanceDataPromise,
      instanceTypeName,
      attributes: attributeNames,
      attributeFilter,
      stateFilter,
      params
    },
    { defer: false }
  );
  return [state, run];
};

const instanceDataPromise = ({
  instanceTypeName,
  attributes,
  attributeFilter,
  stateFilter,
  params
}) => {
  const instQuery = {
    instanceTypeName: instanceTypeName,
    attributes,
    attributeFilter,
    stateFilter,
    getAllowedOperations: true,
    getAllowedActions: true,
    ...params
  };
  const endpoint = createEndpoint({ ...instQuery });
  return { endpoint: endpoint };
};

const fetchInstanceAttachments = ({ instanceId, versionId, params }) => {
  const endpoint = versionId
    ? getInstanceVersionAttachments(instanceId, versionId)
    : instanceAttachmentsURL(instanceId);
  return {
    endpoint: endpoint,
    config: {
      params: { ...params },
      method: 'GET'
    }
  };
};

export function useInstanceImages({ instanceId, params, defer = false }) {
  const { state, run } = useProtrakApi(
    {
      requestConfig: fetchInstanceAttachments,
      instanceId,
      params
    },
    { defer: defer }
  );
  return [state, run];
}

export const useInstanceMessageDetails = instanceId => {
  const { state, run } = useProtrakApi(
    { requestConfig: instanceMessagesPromise, instanceId },
    { defer: false }
  );
  return [state, run];
};

const instanceMessagesPromise = ({ instanceId }) => {
  const url = getInstanceMessagesURL(instanceId);

  return {
    endpoint: url,
    config: {
      method: 'GET'
    }
  };
};

export const useInstance = ({
  instanceId,
  attributes,
  stateFilter,
  attributeFilter,
  relationFilters
}) => {
  const { state, run } = useProtrakApi(
    {
      requestConfig: instancePromise,
      instanceId,
      attributes,
      stateFilter,
      attributeFilter,
      relationFilters
    },
    { defer: false }
  );
  return [state, run];
};

const instancePromise = ({
  instanceId,
  attributes,
  stateFilter,
  attributeFilter,
  relationFilters
}) => {
  const url = getInstanceURL(instanceId);

  return {
    endpoint: url,
    config: {
      method: 'GET',
      params: { attributes, stateFilter, attributeFilter, relationFilters }
    }
  };
};

export const useUploadAndCreateInstance = () => {
  const { state, run } = useProtrakApi(
    { requestConfig: uploadAndCreateInstance },
    { defer: true }
  );
  return [state, run];
};

const uploadAndCreateInstance = ({ payload }) => {
  return {
    endpoint: instanceBase,
    config: {
      method: 'POST',
      data: payload
    }
  };
};

export function useCreateInstance(payload) {
  const { reload } = useAsyncJobContext();
  const onSuccess = () => {
    reload();
  };

  const { state, run } = useProtrakApi(
    { requestConfig: instanceCreatePromise, payload },
    { defer: true, onSuccess }
  );
  return { state, run };
}

const instanceCreatePromise = ({ payload }) => {
  return {
    endpoint: instanceBase,
    config: {
      method: 'POST',
      data: payload
    }
  };
};

export const useUpdateInstance = () => {
  const { state, run } = useProtrakApi(
    { requestConfig: updateInstance },
    { defer: true }
  );
  return [state, run];
};

const updateInstance = ({ modifiedDate, payload }) => {
  const updateUrl = `${instanceBase}?lastModified=${modifiedDate}`;
  return {
    endpoint: updateUrl,
    config: {
      method: 'PUT',
      data: payload
    }
  };
};
